import { useState, useEffect } from 'react';
import { getAllFunds, getICMembersByFund, deactivateICMember } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import Table from '../../shared/UI/Table';
import Badge from '../../shared/UI/Badge';
import Button from '../../shared/UI/Button';
import SelectField from '../../shared/Forms/SelectField';
import ConfirmDialog from '../../shared/UI/ConfirmDialog';

export default function ICMemberList({ onAdd, onEdit }) {
  const { user } = useAuth();
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  function loadMembers(fundId) {
    if (!fundId) { setMembers([]); return; }
    setLoading(true);
    getICMembersByFund(fundId)
      .then(setMembers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadMembers(selectedFundId); }, [selectedFundId]);

  async function handleRemove() {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await deactivateICMember({ memberId: confirmRemove.id, performedBy: user.uid });
      setConfirmRemove(null);
      loadMembers(selectedFundId);
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));

  const columns = [
    { key: 'full_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'is_chair',
      label: 'Is Chair',
      render: row => row.is_chair ? <Badge text="Chair" variant="accent" /> : '—',
    },
    {
      key: 'appointed_at',
      label: 'Appointed Date',
      render: row => row.appointed_at?.toDate
        ? row.appointed_at.toDate().toLocaleDateString() : '—',
    },
    {
      key: 'is_active',
      label: 'Status',
      render: row => <Badge text={row.is_active !== false ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => onEdit(row, selectedFundId)}>Edit</Button>
          {row.is_active !== false && (
            <Button size="sm" variant="danger" onClick={() => setConfirmRemove(row)}>Remove</Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2>IC Members</h2>
          <Button variant="primary" onClick={() => onAdd(selectedFundId)} disabled={!selectedFundId}>
            + Add IC Member
          </Button>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div style={{ maxWidth: 360, marginBottom: 20 }}>
            <SelectField label="Select Fund" value={selectedFundId}
              onChange={e => setSelectedFundId(e.target.value)}
              options={fundOptions} placeholder="Choose a fund…" />
          </div>
          {selectedFundId && (
            <div className="alert alert-info" style={{ fontSize: 13, marginBottom: 16 }}>
              Only users with role "IC Member" can be added here. Create the user first in the Users section.
            </div>
          )}
        </div>
        {selectedFundId && (
          <Table
            columns={columns}
            data={members}
            loading={loading}
            emptyTitle="No IC members configured"
            emptyMessage="Add IC members to this fund."
            emptyAction={{ label: '+ Add IC Member', onClick: () => onAdd(selectedFundId) }}
          />
        )}
        {!selectedFundId && (
          <div className="empty-state">
            <div className="empty-state-icon">🏛️</div>
            <h3>Select a fund</h3>
            <p>Choose a fund to view and manage its IC members.</p>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title="Remove IC Member"
        message={`Remove ${confirmRemove?.full_name} from this fund's IC? Their record will be kept but marked inactive.`}
        confirmLabel="Remove"
        loading={removing}
      />
    </>
  );
}
